/**
 * Override types + Zod schema (PROD-571).
 *
 * The structured override payload captured by `OverrideModal` and emitted as
 * the `override_event` telemetry event. The Zod schema is the single source
 * of truth for the structured-output contract (HC7 LLM Guardrails: Zod is
 * the runtime validator, not just a TypeScript type).
 *
 * Per COMPONENT-CONTRACTS §8 the modal opens when a clinician clicks the
 * Override CTA on an NBA card; the captured event then feeds the learning
 * loop event stream surfaced in PROD-573.
 *
 * HC5: zero em dashes anywhere in the source - tag display labels use
 * hyphens (the canonical reason-category labels are listed verbatim in
 * the ticket spec and reproduced here as `REASON_CATEGORIES`). The Zod
 * `reason_category` enum uses snake_case internal ids per
 * COMPONENT-CONTRACTS §8 so the structured event JSON matches the rest
 * of the telemetry payload shape.
 *
 * HC7 + HC9: every submit runs through `overrideEventSchema.parse` before
 * emit / persist. A schema failure surfaces field-level errors; a passed
 * parse is the only thing that triggers the telemetry sink call.
 */
import { z } from 'zod';

/**
 * Verbatim display labels for the reason-category single-select. The
 * ticket spec quotes these strings exactly; the modal renders them as
 * radio labels and the `value` attribute carries the snake_case id.
 *
 * Order is the canonical render order from the ticket spec.
 */
export const REASON_CATEGORIES = [
  { id: 'patient_preference', label: 'Patient preference' },
  {
    id: 'contraindication_not_visible_to_ai',
    label: 'Contraindication not visible to AI',
  },
  { id: 'insurance_constraint', label: 'Insurance constraint' },
  { id: 'clinical_context', label: 'Clinical context' },
  { id: 'workflow_constraint', label: 'Workflow constraint' },
  { id: 'other', label: 'Other' },
] as const;

export type ReasonCategoryId = (typeof REASON_CATEGORIES)[number]['id'];

const REASON_CATEGORY_IDS = REASON_CATEGORIES.map((c) => c.id) as [
  ReasonCategoryId,
  ...ReasonCategoryId[],
];

/**
 * Verbatim display labels for the patient-context multi-select. Tags are
 * optional. Order is the canonical render order from the ticket spec.
 */
export const PATIENT_CONTEXT_TAGS = [
  { id: 'comorbidity', label: 'Comorbidity' },
  { id: 'medication_interaction', label: 'Medication interaction' },
  { id: 'social_determinant', label: 'Social determinant' },
  { id: 'cultural_factor', label: 'Cultural factor' },
  { id: 'clinical_history', label: 'Clinical history' },
  { id: 'other', label: 'Other' },
] as const;

export type PatientContextTagId = (typeof PATIENT_CONTEXT_TAGS)[number]['id'];

const PATIENT_CONTEXT_TAG_IDS = PATIENT_CONTEXT_TAGS.map((t) => t.id) as [
  PatientContextTagId,
  ...PatientContextTagId[],
];

/** Free-text length budget per the ticket spec. */
export const FREE_TEXT_MAX_LENGTH = 200;
export const ALTERNATIVE_MAX_LENGTH = 200;

/**
 * Required field error message; surfaced in the form via `aria-live`.
 * Wording is canonical per BDD OV-5 in PATHWAY-PRD.md.
 */
export const REASON_CATEGORY_REQUIRED_MESSAGE = 'Reason category is required';
export const FREE_TEXT_REQUIRED_MESSAGE = 'Free text reason is required';
export const FREE_TEXT_TOO_LONG_MESSAGE =
  'Free text reason must be at most 200 characters';
export const ALTERNATIVE_TOO_LONG_MESSAGE =
  'Alternative considered must be at most 200 characters';

/**
 * Form-level Zod schema. The form fields plus the modal context (pathway
 * id, step id, nba id) compose into the canonical `OverrideEvent` via
 * `buildOverrideEvent` below.
 */
export const overrideFormSchema = z.object({
  reason_category: z.enum(REASON_CATEGORY_IDS, {
    errorMap: () => ({ message: REASON_CATEGORY_REQUIRED_MESSAGE }),
  }),
  free_text_reason: z
    .string()
    .trim()
    .min(1, { message: FREE_TEXT_REQUIRED_MESSAGE })
    .max(FREE_TEXT_MAX_LENGTH, { message: FREE_TEXT_TOO_LONG_MESSAGE }),
  alternative_considered: z
    .string()
    .trim()
    .max(ALTERNATIVE_MAX_LENGTH, { message: ALTERNATIVE_TOO_LONG_MESSAGE })
    .optional()
    .or(z.literal('')),
  context_tags: z.array(z.enum(PATIENT_CONTEXT_TAG_IDS)).default([]),
});

export type OverrideFormValues = z.infer<typeof overrideFormSchema>;

/**
 * Canonical event payload. This is the structured object that lands in
 * the telemetry sink as `override_event` and in the
 * `cps:tenant:${tenantId}:overrides` localStorage entry. Snake_case keys
 * match the rest of the prototype's telemetry payload shape.
 */
export const overrideEventSchema = z.object({
  pathway_id: z.string().min(1),
  step_id: z.string().min(1),
  nba_id: z.string().min(1),
  tenant_id: z.string().min(1),
  reason_category: z.enum(REASON_CATEGORY_IDS),
  free_text_reason: z
    .string()
    .trim()
    .min(1)
    .max(FREE_TEXT_MAX_LENGTH),
  alternative_considered: z.string().trim().max(ALTERNATIVE_MAX_LENGTH).nullable(),
  context_tags: z.array(z.enum(PATIENT_CONTEXT_TAG_IDS)),
  /** Derived flag for telemetry rollups; mirrors BDD OV-2. */
  has_alternative_text: z.boolean(),
  /** Derived flag for telemetry rollups; mirrors BDD OV-2. */
  has_context_tags: z.boolean(),
  ts: z.string().min(1),
  correlation_id: z.string().min(1),
});

export type OverrideEvent = z.infer<typeof overrideEventSchema>;

/**
 * Build the canonical `OverrideEvent` from validated form values + the
 * surrounding modal context. Pure function; no side effects.
 */
export function buildOverrideEvent(args: {
  form: OverrideFormValues;
  pathwayId: string;
  stepId: string;
  nbaId: string;
  tenantId: string;
  ts?: string;
  correlationId: string;
}): OverrideEvent {
  const alternativeRaw = args.form.alternative_considered?.trim() ?? '';
  const alternative = alternativeRaw.length > 0 ? alternativeRaw : null;
  const tags = args.form.context_tags ?? [];
  const event: OverrideEvent = {
    pathway_id: args.pathwayId,
    step_id: args.stepId,
    nba_id: args.nbaId,
    tenant_id: args.tenantId,
    reason_category: args.form.reason_category,
    free_text_reason: args.form.free_text_reason.trim(),
    alternative_considered: alternative,
    context_tags: tags,
    has_alternative_text: alternative !== null,
    has_context_tags: tags.length > 0,
    ts: args.ts ?? new Date().toISOString(),
    correlation_id: args.correlationId,
  };
  // Final parse so the contract is enforced at the boundary.
  return overrideEventSchema.parse(event);
}
